import { Component, EventEmitter, Input, Output, computed, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { CartService } from '../../../core/services/cart.service';
import { AuthService } from '../../../core/services/auth.service';
import { ToastService } from '../../../core/services/toast.service';
import { BookLoaderComponent } from '../book-loader/book-loader.component';
import { RippleDirective } from '../../directives/ripple.directive';
import { ImgFadeDirective } from '../../directives/img-fade.directive';
import { cartRowAnim } from '../../animations';
import { toImageSrc } from '../../utils/image.util';

@Component({
  selector: 'app-cart-panel',
  standalone: true,
  imports: [DatePipe, BookLoaderComponent, RippleDirective, ImgFadeDirective],
  templateUrl: './cart-panel.component.html',
  styleUrl: './cart-panel.component.css',
  animations: [cartRowAnim]
})
export class CartPanelComponent {
  @Input() open = false;
  @Output() closePanel = new EventEmitter<void>();
  @Output() authRequired = new EventEmitter<void>();

  readonly total = computed(() =>
    this.cartService.cartItems().reduce((sum, item) => sum + (item.CartLineTotal || 0), 0)
  );
  readonly deliveryFee = computed(() => (this.total() >= 499 || this.total() === 0 ? 0 : 49));
  readonly grandTotal = computed(() => this.total() + this.deliveryFee());
  readonly paymentProcessing = signal(false);
  readonly orderSuccess = signal<{ id: string; delivery: string } | null>(null);
  readonly promoCode = signal('');
  readonly newsletterEmail = signal('');
  readonly newsletterBusy = signal(false);

  constructor(
    public readonly cartService: CartService,
    public readonly authService: AuthService,
    private readonly toast: ToastService,
    private readonly router: Router
  ) {
    const user = this.authService.currentUser();
    if (user?.UsrEmail) {
      this.newsletterEmail.set(user.UsrEmail);
    }
  }

  formatPrice(value: number): string {
    return `₹${Number(value || 0).toFixed(2)}`;
  }

  imageSrc(value?: string | null): string | null {
    return toImageSrc(value);
  }

  onImgError(event: Event): void {
    const el = event.target as HTMLImageElement;
    el.style.display = 'none';
  }

  updateQty(cartId: number, nextQty: number): void {
    if (nextQty < 1) {
      this.remove(cartId);
      return;
    }
    this.cartService.updateQuantity(cartId, nextQty).subscribe({
      next: () => this.reload(),
      error: () => this.toast.push('Failed to update quantity', 'error')
    });
  }

  remove(cartId: number): void {
    this.cartService.remove(cartId).subscribe({ next: () => this.reload() });
  }

  clear(): void {
    const user = this.authService.currentUser();
    if (!user) {
      this.authRequired.emit();
      return;
    }
    this.cartService.clear(user.UsrId).subscribe({ next: () => this.reload() });
  }

  applyPromo(): void {
    // TODO: connect promo codes to backend when available
    const code = this.promoCode().trim();
    if (!code) {
      this.toast.push('Enter a promo code', 'error');
      return;
    }
    this.toast.push('Promo codes will be available soon.', 'success');
  }

  subscribeNewsletter(): void {
    if (this.newsletterBusy()) return;
    const email = this.newsletterEmail().trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.toast.push('Please enter a valid email address.', 'error');
      return;
    }

    this.newsletterBusy.set(true);
    try {
      const key = 'newsletterSubscribers';
      const raw = localStorage.getItem(key);
      const list: string[] = raw ? (JSON.parse(raw) as string[]) : [];
      const normalized = email.toLowerCase();
      if (!list.includes(normalized)) {
        list.push(normalized);
        localStorage.setItem(key, JSON.stringify(list));
      }
      // TODO: connect to backend newsletter / subscription API when available
      this.toast.push('Subscribed! You’ll get our latest offers.', 'success');
      this.newsletterEmail.set('');
    } catch {
      this.toast.push('Could not save subscription. Please try again.', 'error');
    } finally {
      this.newsletterBusy.set(false);
    }
  }

  checkout(): void {
    const user = this.authService.currentUser();
    if (!user) {
      this.authRequired.emit();
      return;
    }
    if (!this.cartService.cartItems().length) return;
    this.closePanel.emit();
    void this.router.navigate(['/checkout']);
  }

  closeSuccess(): void {
    this.orderSuccess.set(null);
  }

  goToOrders(): void {
    this.orderSuccess.set(null);
    this.closePanel.emit();
    void this.router.navigate(['/orders']);
  }

  private reload(): void {
    const user = this.authService.currentUser();
    if (user) this.cartService.loadCart(user.UsrId);
  }
}
